import React, { useEffect, useState } from "react";
import Types from "prop-types";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import withStyles from "@material-ui/core/styles/withStyles";
import Typography from "@material-ui/core/Typography";
import { Helmet } from "react-helmet";
import { withTranslation } from "react-i18next";

import { ReCaptcha } from "../../Guard";
import { verifyApproveRequestStatus } from "../../gateway/LoginGateway";
import { maskPhoneNumber } from "../../common/PhoneNumber";
import { useTimer } from "../../otp/Timer";
import Analytika from "../../../analytika";
import styles from "./styles";

const DEFAULT_RETRY_TIMEOUT = 30;
const MAX_RETRIES = 5;
let currentRetries = 0;

function PushNotificationView(props) {
  const {
    classes,
    phoneNumber,
    merchant,
    token,
    onConfirm,
    onReject,
    doLogin,
  } = props;
  const [pushIdentifier, setPushIdentifier] = useState(token?.id);

  useEffect(() => {
    Analytika.fireEvent("push_screen_opened");
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => {
        verifyApproveRequestStatus(pushIdentifier)
          .then(({ status }) => {
            switch (status) {
              case "confirmed":
                onConfirm(pushIdentifier);
                break;
              case "rejected":
                onReject();
                break;
            }
          })
          .catch(() => {
            clearInterval(interval);
          });
      },
      currentRetries < 2 ? 2000 : 5000
    );
    return () => clearInterval(interval);
  }, [pushIdentifier, onReject, onConfirm]);

  const onRetry = async () => {
    Analytika.fireEvent("retry_button_clicked", {
      currentRetries,
    });
    if (currentRetries < MAX_RETRIES) {
      doLogin({ is_retry: true }).then((retryToken) => {
        setPushIdentifier(retryToken?.id);
        currentRetries++;
      });
    } else {
      onReject("maxTriesReached");
    }
  };

  return (
    <form>
      <Helmet>
        <title>Push Notification</title>
      </Helmet>
      <Box display="flex">
        <Box m="auto">
          <CircularProgress color="inherit" />
        </Box>
      </Box>
      <div className={classes.title}>
        <Typography component="h1" className={classes.formTitle}>
          We’ve sent you a notification
        </Typography>
        <Typography component="h3" className={classes.formSubtitle}>
          {maskPhoneNumber(phoneNumber)}
        </Typography>
        <Typography component="h3" className={classes.formSubtitle}>
          Open Careem app to continue checkout on <span>{merchant}</span>.
        </Typography>
      </div>
      <SendAgainButton onClick={onRetry} />
      <ReCaptcha />
    </form>
  );
}

const SendAgainButton = withStyles(styles)(({ classes, onClick }) => {
  let [retrySeconds, setRetrySeconds] = useTimer(DEFAULT_RETRY_TIMEOUT);

  let clickAndResetTimer = (event) => {
    onClick(event);
    setRetrySeconds(DEFAULT_RETRY_TIMEOUT);
  };

  return (
    <div className={classes.sendAgainContainer}>
      <Button
        id="btn-send-again"
        type="submit"
        onClick={clickAndResetTimer}
        disabled={retrySeconds > 0}
        classes={{
          root: classes.sendAgainButton,
          disabled: classes.sendAgainButtonDisabled,
          text: classes.sendAgainButtonText,
        }}
        size="large"
        {...(retrySeconds > 0 && {
          startIcon: (
            <span className={classes.sendAgainButtonIcon}>{retrySeconds}</span>
          ),
        })}
      >
        send again
      </Button>
    </div>
  );
});

SendAgainButton.propTypes = {
  onClick: Types.func.isRequired,
};

PushNotificationView.propTypes = {
  classes: Types.object.isRequired,
  token: Types.object.isRequired,
  phoneNumber: Types.string.isRequired,
  merchant: Types.string.isRequired,
  onConfirm: Types.func.isRequired,
  onReject: Types.func.isRequired,
  doLogin: Types.func.isRequired,
};

export default withTranslation()(withStyles(styles)(PushNotificationView));
